import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowLeft, MonitorSmartphone, FileDown } from 'lucide-react';
import { Navbar, Footer } from '@/components/Layout';
import { useClaimDemo } from '@/lib/useClaimDemo';
import { DemoClaimForm } from '@/components/spec/demo/DemoClaimForm';
import { DemoReceipt } from '@/components/spec/demo/DemoReceipt';
import { DemoTimeline } from '@/components/spec/demo/DemoTimeline';
import { DemoSellerCheck } from '@/components/spec/demo/DemoSellerCheck';
import { DemoChargeMatch } from '@/components/spec/demo/DemoChargeMatch';
import { DemoHotelCancel } from '@/components/spec/demo/DemoHotelCancel';
import { DemoTakeover } from '@/components/spec/demo/DemoTakeover';
import { DemoWrongItem } from '@/components/spec/demo/DemoWrongItem';

const KIT = '/downloads/kojiru-claim-records-reference-kit-draft-0.4.zip';

export default function ClaimRecordDemo() {
  const demo = useClaimDemo();
  const [prefill, setPrefill] = React.useState(null);
  const handleUseOrder = (order, charge) => {
    setPrefill({ orderRef: order.orderRef, notice: `Charge ${charge.name} matched to this order` });
    document.querySelector('[data-testid="demo-form"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="claim-demo-page">
      <Helmet><title>Claim Records Demo | Draft 0.4 Proposal | Kojiru</title></Helmet>
      <Navbar />
      <main className="pt-28 pb-20 px-6">
        <div className="container mx-auto max-w-6xl">
          <Link to="/specs/claim-records" className="inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-white transition-colors mb-8" data-testid="demo-back-link"><ArrowLeft className="w-3.5 h-3.5" /> Claim Records</Link>
          <div className="text-xs uppercase tracking-widest text-amber-300 mb-3">Draft 0.4 proposal · Reference kit demo</div>
          <h1 className="text-4xl sm:text-5xl font-bold leading-tight mb-4" data-testid="demo-title">Try a claim in your browser</h1>
          <p className="text-base text-gray-400 max-w-3xl leading-relaxed mb-6">Send a test cancellation claim to a pretend merchant and watch what the reference kit does: a check that the seller agent truly acts for the store, a signed delivery receipt, one case per claim ID, safe retries, a decision, and one contest.</p>
          <div className="rounded-2xl border border-sky-500/30 bg-sky-500/[0.05] p-5 flex gap-3 mb-6 max-w-3xl" data-testid="demo-local-notice">
            <MonitorSmartphone className="w-5 h-5 text-sky-300 shrink-0 mt-0.5" />
            <p className="text-sm text-sky-100/80 leading-relaxed">Everything runs in this browser tab with a throwaway test key. Nothing you enter or create here is sent to Kojiru or any server, and it all disappears when you close the tab. Test data only. Signature checks on the claim are skipped, as in the kit. Not a service and not a determination by Kojiru.</p>
          </div>
          <a href={KIT} download className="inline-flex items-center gap-1.5 text-xs text-amber-300 hover:text-amber-200 mb-8" data-testid="demo-kit-download"><FileDown className="w-3.5 h-3.5" /> Download the reference kit (ZIP) to run it yourself</a>
          <DemoChargeMatch onUseOrder={handleUseOrder} />
          <DemoHotelCancel demo={demo} />
          <DemoTakeover demo={demo} />
          <DemoWrongItem />
          <DemoSellerCheck demo={demo} />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-6 min-w-0">
              <DemoClaimForm onSubmit={demo.submit} sent={!!demo.current} prefill={prefill} />
              <DemoTimeline demo={demo} />
            </div>
            <div className="min-w-0"><DemoReceipt current={demo.current} merchantKey={demo.merchantKey} log={demo.log} /></div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
